import * as z from 'zod'

import type { EventBlock, EventField } from '../schema/block'
import { getApiNameFromTitle } from '../../api-kv'
import { AtLeast } from '.'
import { RangeEnum, TimeSeriesRange } from '../schema/user-preferences'

// API Name Uniqueness
export function checkIfFieldNameExists(
  field: Pick<EventField, 'name'> & { id: string },
  schema: EventBlock['constraints']['schema']
): boolean {
  return Object.entries(schema).some(([id, sibling]) => {
    if (!sibling) return false
    return id !== field.id && sibling.name === field.name
  })
}

export function getUniqueFieldName(
  field: AtLeast<EventField, 'label'> & {
    name?: string
    id: string
  },
  schema: EventBlock['constraints']['schema']
): { name: string; label: string } {
  let uniqueName = field.name
  let label = field.label
  const siblingsArray = Object.entries(schema)

  if (uniqueName) {
    if (
      siblingsArray.some(([id, v]) => {
        if (!v) return false
        return id !== field.id && v.name === field.name
      })
    ) {
      throw new Error("Can't have duplicate API names")
    }
  }

  if (!label) {
    label = 'Untitled'
  }

  if (!uniqueName) {
    uniqueName = getApiNameFromTitle(label)
    if (
      siblingsArray.some(([id, v]) => {
        if (!v) return false
        return id !== field.id && v.name === uniqueName
      })
    ) {
      uniqueName = `${uniqueName}_${field.id.slice(0, 4)}`
    }
  }
  return { name: uniqueName, label }
}

export function withUniqueFieldNames(
  schema: EventBlock['constraints']['schema']
): (EventField & { id: string })[] {
  const copy = Object.entries(schema)
    .filter((item): item is [string, EventField] => !!item[1])
    .map(([id, field]) => ({ ...field, id }))
    .sort((a, b) => a.order - b.order)
  for (const field of copy) {
    const result = getUniqueFieldName(field, schema)
    field.name = result.name
    field.label = result.label
  }
  return copy
}

export const fillTimePeriod = (
  events: EventNode[],
  range: TimeSeriesRange
): { allBreakpoints: EventNode[]; total: number } => {
  const allBreakpoints: EventNode[] = []
  let total = 0

  switch (range) {
    case RangeEnum.day:
      // last 24 hours
      for (let i = 0; i < 24; i++) {
        const isoDate = new Date(new Date().getTime() - 1000 * 60 * 60 * i)
          .toISOString()
          .replace(/T(\d\d).*/, 'T$1:00:00.000Z') // round to the hour
        const existingValue = events.find((e) => e.label === isoDate)?.count
        total += existingValue ?? 0
        allBreakpoints.unshift({
          count: existingValue ?? 0,
          label: isoDate,
        })
      }
      break
    case RangeEnum.week:
    case RangeEnum.month:
    case RangeEnum.year:
    case RangeEnum.allTime: {
      const days =
        range === RangeEnum.week ? 7 : range === RangeEnum.month ? 30 : 365

      // last n days
      for (let i = 0; i < days; i++) {
        const isoDate = new Date(new Date().getTime() - 1000 * 60 * 60 * 24 * i)
          .toISOString()
          .replace(/T.*/, 'T00:00:00.000Z') // round to the day
        const existingValue = events.find((e) => e.label === isoDate)?.count
        total += existingValue ?? 0
        allBreakpoints.unshift({
          count: existingValue ?? 0,
          label: isoDate,
        })
      }
      break
    }
  }

  return { allBreakpoints, total }
}

export const eventsResponseSchema = z
  .object({
    success: z.literal(true),
    data: z.array(
      z.object({
        count: z.number(),
        label: z.string(),
      })
    ),
  })
  .or(z.object({ success: z.literal(false), error: z.string() }))

export type EventNode = {
  count: number
  label: string
}
